import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { Bell, ShoppingCart, AlertTriangle, CreditCard, CalendarClock, Clock, CheckCircle2, Check, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const ICONS = {
  new_orders: ShoppingCart,
  low_stock: AlertTriangle,
  credit_exceeded: CreditCard,
  day_end_pending: CalendarClock,
  order_active: Clock,
};

const SEV = {
  info: "text-brand-gold bg-brand-gold/10",
  warning: "text-amber-600 bg-amber-50",
  alert: "text-brand-alert bg-brand-alert/10",
};

export function NotificationBell({ variant = "dark" }) {
  const [data, setData] = useState({ count: 0, total: 0, items: [] });
  const [open, setOpen] = useState(false);
  const timer = useRef();

  const load = async () => {
    try { const { data } = await api.get("/notifications"); setData(data); } catch {}
  };

  useEffect(() => {
    load();
    timer.current = setInterval(load, 30000);
    return () => clearInterval(timer.current);
  }, []);

  const markAll = async () => {
    try { await api.post("/notifications/read", { all: true }); await load(); } catch {}
  };
  const dismiss = async (sig) => {
    try { await api.post("/notifications/read", { signatures: [sig] }); await load(); } catch {}
  };

  const btnColor = variant === "dark"
    ? "text-brand-bg/70 hover:text-white hover:bg-white/10"
    : "text-brand-text hover:bg-brand-bg";

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) load(); }}>
      <PopoverTrigger asChild>
        <button data-testid="notification-bell" className={`relative w-9 h-9 rounded-sm flex items-center justify-center transition-colors ${btnColor}`}>
          <Bell className="w-[18px] h-[18px]" />
          {data.count > 0 && (
            <span data-testid="notification-count" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-brand-alert text-white text-[10px] font-bold flex items-center justify-center">
              {data.count}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0 bg-white border-brand-line" data-testid="notification-panel">
        <div className="px-4 py-3 border-b border-brand-line flex items-center justify-between gap-2">
          <p className="font-semibold text-brand-text text-sm">Notifications</p>
          {data.count > 0 ? (
            <button onClick={markAll} data-testid="notification-mark-all" className="text-xs text-brand-primary font-semibold hover:underline inline-flex items-center gap-1">
              <Check className="w-3.5 h-3.5" /> Mark all read
            </button>
          ) : <span className="text-xs text-brand-muted">{data.total} total</span>}
        </div>
        <div className="max-h-[360px] overflow-y-auto">
          {data.items.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-brand-muted" data-testid="notification-empty">
              <CheckCircle2 className="w-8 h-8 mb-2 text-brand-ready" />
              <p className="text-sm">You're all caught up.</p>
            </div>
          ) : data.items.map((n) => {
            const Icon = ICONS[n.type] || Bell;
            return (
              <div key={n.sig} data-testid={`notification-item-${n.type}`} className={`group flex gap-3 px-4 py-3 border-b border-brand-line last:border-0 hover:bg-brand-bg/40 ${n.read ? "opacity-55" : ""}`}>
                <div className={`w-8 h-8 rounded-sm flex items-center justify-center shrink-0 ${SEV[n.severity] || SEV.info}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-brand-alert shrink-0" data-testid="notification-unread-dot" />}
                    <p className="text-sm font-semibold text-brand-text truncate">{n.title}</p>
                  </div>
                  <p className="text-xs text-brand-muted leading-snug">{n.detail}</p>
                </div>
                {!n.read && (
                  <button onClick={() => dismiss(n.sig)} data-testid={`notification-dismiss-${n.type}`} title="Dismiss"
                    className="shrink-0 w-6 h-6 rounded-sm flex items-center justify-center text-brand-muted hover:text-brand-alert hover:bg-brand-bg opacity-0 group-hover:opacity-100 transition-opacity">
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
